/**
 * ConsumptionHistory - 消费记录 Tab
 */
import { useState, useEffect } from 'react';
import { ShoppingBag, BookOpen, Gift, Wallet, RotateCcw, Search } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface Transaction {
  id: string;
  type: string;
  amount: number;
  balance: number;
  description: string;
  createdAt: string;
}

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

const typeMeta: Record<string, { label: string; icon: any; color: string }> = {
  consumption: { label: '消费', icon: BookOpen, color: 'text-red-500' },
  recharge: { label: '充值', icon: Wallet, color: 'text-green-500' },
  trial_bonus: { label: '体验金', icon: Gift, color: 'text-amber-500' },
  purchase: { label: '购套餐', icon: ShoppingBag, color: 'text-indigo-500' },
  refund: { label: '退款', icon: RotateCcw, color: 'text-blue-500' },
};

export default function ConsumptionHistory() {
  const { isAuthenticated } = useAuth();
  const [records, setRecords] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchTransactions = async (currentPage: number, currentStart?: string, currentEnd?: string) => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: String(currentPage),
        pageSize: String(pageSize),
      });
      if (currentStart) params.append('startDate', currentStart);
      if (currentEnd) params.append('endDate', currentEnd);

      const res = await fetch(`${API_BASE_URL}/user/transactions?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (data.success) {
        setRecords(data.data?.list || []);
        setPagination(data.data?.pagination || null);
      } else {
        console.error('获取消费记录失败:', data.error);
      }
    } catch (error) {
      console.error('获取消费记录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleSearch = () => {
    setPage(1);
    fetchTransactions(1, startDate || undefined, endDate || undefined);
  };

  const handlePageChange = (newPage: number) => {
    if (!pagination || newPage < 1 || newPage > pagination.totalPages) return;
    setPage(newPage);
    fetchTransactions(newPage, startDate || undefined, endDate || undefined);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {/* 日期筛选栏 */}
        <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl p-4 border border-slate-200">
          <span className="text-sm text-slate-600">日期范围：</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
          />
          <span className="text-slate-400">至</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
          />
          <button
            onClick={handleSearch}
            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            <Search className="w-4 h-4" />
            查询
          </button>
        </div>
        <div className="text-center py-20">
          <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">暂无消费记录</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* 日期筛选栏 */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl p-4 border border-slate-200">
        <span className="text-sm text-slate-600">日期范围：</span>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
        />
        <span className="text-slate-400">至</span>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
        />
        <button
          onClick={handleSearch}
          className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
        >
          <Search className="w-4 h-4" />
          查询
        </button>
        {pagination && (
          <span className="ml-auto text-sm text-slate-500">
            共 {pagination.total} 条记录
          </span>
        )}
      </div>

      {/* 记录列表 */}
      <div className="flex flex-col gap-3">
        {records.map((record) => {
          const meta = typeMeta[record.type] || { label: record.type, icon: ShoppingBag, color: 'text-slate-500' };
          const Icon = meta.icon;
          const isIncome = record.amount > 0;
          return (
            <div key={record.id} className="flex items-center bg-slate-50 rounded-xl p-4 gap-4">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Icon className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-slate-800 truncate">{record.description}</h4>
                <p className="text-sm text-slate-500">
                  {new Date(record.createdAt).toLocaleString()} · <span className={meta.color}>{meta.label}</span>
                </p>
              </div>
              <div className="text-right">
                <div className={`font-medium ${isIncome ? 'text-green-500' : 'text-red-500'}`}>
                  {isIncome ? '+' : ''}{record.amount.toFixed(2)}元
                </div>
                <div className="text-xs text-slate-400">余额 ¥{record.balance.toFixed(2)}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 分页 */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            上一页
          </button>
          <span className="text-sm text-slate-600">
            第 {page} / {pagination.totalPages} 页
          </span>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= pagination.totalPages}
            className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            下一页
          </button>
        </div>
      )}
    </div>
  );
}
